import type { PlaycastTouchEvent, PlaycastTouch } from '@playcastdotio/Messaging';

/**
 * Touch Input Helper Functions
 *
 * Similar to mobileGamepadToGamepadStateMessage for mobile gamepad,
 * these helpers transform browser TouchEvent data into Playcast message format.
 */

/**
 * Calculate the actual content bounds within a video element,
 * accounting for letterboxing (black bars on top/bottom) or
 * pillarboxing (black bars on sides).
 *
 * Assumes 16:9 aspect ratio for the game content.
 */
interface ContentBounds {
  left: number;
  top: number;
  width: number;
  height: number;
  offsetX: number;  // Black bar width on each side (pillarbox)
  offsetY: number;  // Black bar height on top/bottom (letterbox)
}

const calculateContentBounds = (
  rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
): ContentBounds | null => {
  if (rect.width === 0 || rect.height === 0) {
    return null;
  }

  // Assume 16:9 content aspect ratio (common for games)
  const CONTENT_ASPECT_RATIO = 16 / 9;
  const elementAspectRatio = rect.width / rect.height;

  let contentWidth: number;
  let contentHeight: number;
  let offsetX = 0;
  let offsetY = 0;

  if (elementAspectRatio > CONTENT_ASPECT_RATIO) {
    // Element is wider than 16:9 → pillarboxing (black bars on sides)
    contentHeight = rect.height;
    contentWidth = contentHeight * CONTENT_ASPECT_RATIO;
    offsetX = (rect.width - contentWidth) / 2;
  } else {
    // Element is taller than 16:9 → letterboxing (black bars on top/bottom)
    contentWidth = rect.width;
    contentHeight = contentWidth / CONTENT_ASPECT_RATIO;
    offsetY = (rect.height - contentHeight) / 2;
  }

  return {
    left: rect.left + offsetX,
    top: rect.top + offsetY,
    width: contentWidth,
    height: contentHeight,
    offsetX,
    offsetY,
  };
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * Maps a viewport-space touch point into the video's local (un-rotated) content
 * frame when the video is CSS force-rotated in mobile fullscreen, returning
 * 0–1 normalised coordinates (with 16:9 letterbox handling).
 *
 * The rotated video is centered at the viewport center (top/left:50% +
 * translate(-50%,-50%)) with a local box of width:100vh x height:100vw, so we
 * can invert the rotation around the viewport center with no DOM reads.
 * `getBoundingClientRect()` on the rotated element returns the post-rotation
 * axis-aligned box, which is why the plain rect-based path maps every touch to
 * the bottom-right corner in fullscreen — this path avoids that.
 *
 * Returns null for unsupported angles or a zero-size viewport so the caller can
 * fall back to its default (rect / window) path.
 */
const rotatedTouchToRelative = (
  clientX: number,
  clientY: number,
  rotationDeg: number,
): { relativeX: number; relativeY: number } | null => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  if (viewportWidth === 0 || viewportHeight === 0) return null;

  const dx = clientX - viewportWidth / 2;
  const dy = clientY - viewportHeight / 2;

  // Inverse of the CSS `rotate(rotationDeg)` (clockwise, y-down) applied to the
  // video, mapping the viewport delta back into the video's local frame.
  let localDeltaX: number;
  let localDeltaY: number;
  if (rotationDeg === 90) {
    localDeltaX = dy;
    localDeltaY = -dx;
  } else if (rotationDeg === -90 || rotationDeg === 270) {
    localDeltaX = -dy;
    localDeltaY = dx;
  } else {
    return null;
  }

  // Local (un-rotated) box dimensions mirror the CSS: width:100vh, height:100vw.
  const localWidth = viewportHeight;
  const localHeight = viewportWidth;

  const localX = localWidth / 2 + localDeltaX;
  const localY = localHeight / 2 + localDeltaY;

  const contentBounds = calculateContentBounds({ left: 0, top: 0, width: localWidth, height: localHeight });

  if (contentBounds) {
    return {
      relativeX: clamp01((localX - contentBounds.left) / contentBounds.width),
      relativeY: clamp01((localY - contentBounds.top) / contentBounds.height),
    };
  }

  return {
    relativeX: clamp01(localX / localWidth),
    relativeY: clamp01(localY / localHeight),
  };
};

/**
 * Converts a browser Touch object to PlaycastTouch format.
 *
 * @param touch       - The browser Touch object
 * @param rect        - Optional bounding rect of the target element.
 *                      When provided, `relativeX`/`relativeY` are computed as
 *                      normalised 0–1 coordinates within the element,
 *                      accounting for letterboxing/pillarboxing (16:9 content).
 *                      When omitted, they use window dimensions as fallback.
 * @param rotationDeg - Active CSS rotation of the target element in degrees
 *                      (0, 90, or -90). When non-zero, coordinates are mapped
 *                      through the un-rotated local frame so touches map
 *                      correctly in mobile fullscreen. Defaults to 0.
 */
export const browserTouchToPlaycastTouch = (touch: Touch, rect?: DOMRect, rotationDeg = 0): PlaycastTouch => {
  let relativeX: number;
  let relativeY: number;

  const rotated = rotationDeg !== 0 ? rotatedTouchToRelative(touch.clientX, touch.clientY, rotationDeg) : null;

  if (rotated) {
    relativeX = rotated.relativeX;
    relativeY = rotated.relativeY;
  } else if (rect && rect.width > 0 && rect.height > 0) {
    const contentBounds = calculateContentBounds(rect);

    if (contentBounds) {
      relativeX = clamp01((touch.clientX - contentBounds.left) / contentBounds.width);
      relativeY = clamp01((touch.clientY - contentBounds.top) / contentBounds.height);
    } else {
      relativeX = clamp01((touch.clientX - rect.left) / rect.width);
      relativeY = clamp01((touch.clientY - rect.top) / rect.height);
    }
  } else {
    relativeX = clamp01(touch.clientX / window.innerWidth);
    relativeY = clamp01(touch.clientY / window.innerHeight);
  }

  return {
    identifier: touch.identifier,
    screenX: touch.screenX,
    screenY: touch.screenY,
    relativeX,
    relativeY,
    force: Math.min(1, Math.max(0, touch.force)),
  };
};

/**
 * Converts a browser TouchEvent to PlaycastTouchEvent format.
 *
 * @param event         - The browser TouchEvent
 * @param targetElement - Optional element used to compute element-relative
 *                        `relativeX`/`relativeY` (0–1) for each touch.
 * @param rotationDeg   - Active CSS rotation of the target element in degrees
 *                        (0, 90, or -90) for correct mapping when the video is
 *                        force-rotated in mobile fullscreen. Defaults to 0.
 */
export const browserTouchEventToPlaycastTouchEvent = (
  event: TouchEvent,
  targetElement?: HTMLElement | null,
  rotationDeg = 0,
): PlaycastTouchEvent => {
  const rect = targetElement?.getBoundingClientRect();
  const changedTouches = Array.from(event.changedTouches).map((t) =>
    browserTouchToPlaycastTouch(t, rect, rotationDeg),
  );

  return {
    changedTouches,
    altKey: event.altKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    shiftKey: event.shiftKey,
  };
};

/**
 * Normalizes touch coordinates relative to a target element
 * Useful when you need element-relative coordinates instead of viewport coordinates
 */
export const normalizeTouchCoordinates = (
  touch: Touch,
  element: HTMLElement
): { x: number; y: number } => {
  const rect = element.getBoundingClientRect();
  return {
    x: touch.clientX - rect.left,
    y: touch.clientY - rect.top,
  };
};

/**
 * Gets all active touches from a TouchEvent (not just changed ones)
 * Useful for tracking multi-touch state
 */
export const getAllActiveTouches = (
  event: TouchEvent,
  targetElement?: HTMLElement | null,
): PlaycastTouch[] => {
  const rect = targetElement?.getBoundingClientRect();
  return Array.from(event.touches).map((t) => browserTouchToPlaycastTouch(t, rect));
};

/**
 * Creates a PlaycastTouchEvent with pixel-offset normalized coordinates
 * relative to an element. Useful for canvas-based touch interfaces.
 */
export const browserTouchEventToPlaycastTouchEventNormalized = (
  event: TouchEvent,
  targetElement: HTMLElement,
): PlaycastTouchEvent & { normalizedTouches: Array<PlaycastTouch & { normalizedX: number; normalizedY: number }> } => {
  const rect = targetElement.getBoundingClientRect();

  const changedTouches = Array.from(event.changedTouches).map((t) =>
    browserTouchToPlaycastTouch(t, rect),
  );

  const normalizedTouches = Array.from(event.changedTouches).map((touch) => ({
    ...browserTouchToPlaycastTouch(touch, rect),
    normalizedX: touch.clientX - rect.left,
    normalizedY: touch.clientY - rect.top,
  }));

  return {
    changedTouches,
    altKey: event.altKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    shiftKey: event.shiftKey,
    normalizedTouches,
  };
};
